import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetchJson } from '../../apiClient';
import './AchievementsView.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

const CATEGORIES = ['All', 'Exploration', 'Tasting', 'Cellar', 'Streak'];

function AchievementsView({ instance }) {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [profileResult, achievementsResult] = await Promise.allSettled([
        apiFetchJson(instance, '/api/wineprofile'),
        apiFetchJson(instance, '/api/wineprofile/achievements'),
      ]);
      if (profileResult.status === 'fulfilled') setProfile(profileResult.value);
      if (achievementsResult.status === 'fulfilled') setAchievements(unwrap(achievementsResult.value));
    } catch (e) {
      console.error('Failed to load achievements:', e);
      setError(t('grapeEscape.achievements.loadError', 'Failed to load achievements.'));
    } finally {
      setLoading(false);
    }
  }, [instance, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="ge-loading">
        <div className="ge-spinner" />
        <span>{t('grapeEscape.achievements.loading', 'Loading achievements...')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ge-error">
        <span>{error}</span>
        <button onClick={loadData}>↻</button>
      </div>
    );
  }

  const xp = profile?.xp || 0;
  const level = profile?.level || 1;
  const levelTitle = profile?.levelTitle || 'Novice';
  const xpForNext = profile?.xpForNextLevel || 100;
  const xpProgress = xpForNext > 0 ? Math.min((xp / xpForNext) * 100, 100) : 0;

  const filteredAchievements = categoryFilter === 'All'
    ? achievements
    : achievements.filter(a => a.category === categoryFilter);

  const earned = filteredAchievements.filter(a => a.isCompleted);
  const unearned = filteredAchievements.filter(a => !a.isCompleted);

  return (
    <div className="ge-achievements">
      {/* Level Display */}
      <div className="ge-level-card ge-card">
        <div className="ge-level-display">
          <span className="ge-level-num">{level}</span>
          <div className="ge-level-info">
            <span className="ge-level-title-text">{levelTitle}</span>
            <span className="ge-level-xp">{xp} / {xpForNext} XP</span>
          </div>
        </div>
        <div className="ge-xp-bar">
          <div className="ge-xp-fill" style={{ width: `${xpProgress}%` }} />
        </div>
      </div>

      {/* Category Filter */}
      <div className="ge-achievement-filters">
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            className={`ge-filter-chip ${categoryFilter === cat ? 'active' : ''}`}
            onClick={() => setCategoryFilter(cat)}
          >
            {t(`grapeEscape.achievements.category.${cat.toLowerCase()}`, cat)}
          </button>
        ))}
      </div>

      {/* Achievement Badges */}
      <div className="ge-badge-grid">
        {earned.map(a => (
          <div key={a.id} className="ge-badge-card ge-card earned">
            <span className="ge-badge-icon">{a.icon || '🏆'}</span>
            <span className="ge-badge-name">{a.name}</span>
            <span className="ge-badge-desc">{a.description}</span>
          </div>
        ))}
        {unearned.map(a => (
          <div key={a.id} className="ge-badge-card ge-card locked">
            <span className="ge-badge-icon">{a.icon || '🔒'}</span>
            <span className="ge-badge-name">{a.name}</span>
            <span className="ge-badge-desc">{a.description}</span>
            {a.progress != null && a.targetValue > 0 && (
              <div className="ge-badge-progress">
                <div className="ge-badge-progress-bar">
                  <div
                    className="ge-badge-progress-fill"
                    style={{ width: `${Math.min((a.progress / a.targetValue) * 100, 100)}%` }}
                  />
                </div>
                <span className="ge-badge-progress-text">
                  {a.progress}/{a.targetValue} ({Math.round((a.progress / a.targetValue) * 100)}%)
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredAchievements.length === 0 && (
        <div className="ge-empty">
          {t('grapeEscape.achievements.empty', 'No achievements in this category yet.')}
        </div>
      )}

      {/* Stats Section */}
      <div className="ge-section">
        <h3>{t('grapeEscape.achievements.stats', 'Stats')}</h3>
        <div className="ge-stats-detail-grid">
          <div className="ge-stat-detail ge-card">
            <span className="ge-stat-detail-value">{profile?.totalTastings ?? 0}</span>
            <span className="ge-stat-detail-label">{t('grapeEscape.achievements.totalTastings', 'Total Tastings')}</span>
          </div>
          <div className="ge-stat-detail ge-card">
            <span className="ge-stat-detail-value">{profile?.uniqueGrapes ?? 0}</span>
            <span className="ge-stat-detail-label">{t('grapeEscape.achievements.uniqueGrapes', 'Unique Grapes')}</span>
          </div>
          <div className="ge-stat-detail ge-card">
            <span className="ge-stat-detail-value">{profile?.countriesExplored ?? 0}</span>
            <span className="ge-stat-detail-label">{t('grapeEscape.achievements.countries', 'Countries')}</span>
          </div>
          <div className="ge-stat-detail ge-card">
            <span className="ge-stat-detail-value">{profile?.regionsExplored ?? 0}</span>
            <span className="ge-stat-detail-label">{t('grapeEscape.achievements.regions', 'Regions')}</span>
          </div>
          <div className="ge-stat-detail ge-card">
            <span className="ge-stat-detail-value">{profile?.currentStreak ?? 0}</span>
            <span className="ge-stat-detail-label">{t('grapeEscape.achievements.currentStreak', 'Current Streak')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AchievementsView;
